import React from "react";
import { Button } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import PillPicker from "../PillPicker";
import Summary, { SummaryField, SummaryPills, Actions } from "../Summary";

/**
 * "The four quadrants" — words describing your life now.
 *
 * This question's stored description is one block containing both the
 * instruction AND the per-quadrant guidance. Showing it whole and then
 * repeating the guidance beside each quadrant says everything twice, so the
 * bubble gets the instruction and each quadrant gets its own paragraph. The
 * hint wording below is the course's own, lifted from that description
 * verbatim — not a paraphrase.
 */
const PROMPT =
  "Fill each of the four quadrants with words about your life as it is now. Choose at least three in each — from the suggestions, or type your own.";

export const QUADRANTS = [
  {
    key: "emotional",
    label: "Emotion",
    hint: "Anything you can say about your life that would start with “I feel…”",
  },
  {
    key: "physical",
    label: "Physical",
    hint: "Your concrete situation — location, health, nutrition, finances and so on.",
  },
  {
    key: "intellectual",
    label: "Intellect",
    hint: "Anything you’re interested in, learning, reading, watching, or consuming. What you fill your mind with.",
  },
  {
    key: "spiritual",
    label: "Spirit",
    hint: "What you believe — purpose, right and wrong, how life should be lived, and how you feel about what you’re doing with yours.",
  },
];

const MINIMUM_PER_QUADRANT = 3;

const QuadrantsDeconstruction: React.FC<WidgetProps> = ({
  payload,
  update,
  submit,
  submitting,
  finished,
  question,
  currentUser,
}) => {
  const setQuadrant = (key: string) => (next: string[]) =>
    update({ ...payload, [key]: next });

  const complete = QUADRANTS.every(
    (q) => (payload[q.key] || []).length >= MINIMUM_PER_QUADRANT,
  );

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser} prompt={PROMPT}>
        {QUADRANTS.map((quadrant) => (
          <SummaryField key={quadrant.key} label={quadrant.label}>
            <SummaryPills values={payload[quadrant.key] || []} />
          </SummaryField>
        ))}
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} fallback={PROMPT} />

      {QUADRANTS.map((quadrant) => (
        <div key={quadrant.key} className="ix-section">
          <div className="ix-label">{quadrant.label}</div>
          <p className="ix-hint">{quadrant.hint}</p>
          <PillPicker
            selected={payload[quadrant.key] || []}
            options={payload[`${quadrant.key}_options`] || []}
            onChange={setQuadrant(quadrant.key)}
            placeholder="Add your own"
          />
        </div>
      ))}

      <Actions>
        <Button
          primary
          circular
          className="challenge-submit"
          disabled={!complete || submitting}
          loading={submitting}
          onClick={submit}
        >
          Submit
        </Button>
      </Actions>
    </div>
  );
};

export default QuadrantsDeconstruction;
